import { humanDate } from "./format";
import { FEED_LABEL } from "./kpi";

/**
 * C1 v1.60 (GATE-1089, c7-feed-staleness-alerts C2 v1.56): the open issues of the feeds behind a served figure, exactly as C2
 * names them on the grid envelope, a projection and the Price Run reads. `why` is C2's fact; the portal words the note around it.
 * AMBER, UNKNOWN and UNREGISTERED are never issues (the data integrity monitor owns them), so C2 never sends them.
 */
export type FeedIssueState = "FAILED" | "STALE" | "UNMATCHED" | "RED";
export type FeedIssue = { feed: string; source_key: string | null; state: FeedIssueState; since: string | null; last_good: string | null; why: string };

/** One issue's identity: every field C2 sends, so two issues that differ in any of them both show. */
export const issueKey = (i: FeedIssue) => [i.feed, i.source_key ?? "", i.state, i.since ?? "", i.last_good ?? "", i.why].join("|");

/** Every issue of the answers on screen, once each and in their order: the grids of several markets name the same store-wide feeds. */
export function mergeIssues(...lists: Array<FeedIssue[] | null | undefined>): FeedIssue[] {
  const seen = new Set<string>();
  return lists.flatMap((list) => list ?? []).filter((i) => {
    const key = issueKey(i);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export const feedName = (feed: string) => FEED_LABEL[feed] ?? feed;

/** The GATE-1089 rule: the feed is stale, since when, its last good date and why. A Google Ads account C2 cannot map has no
 * last good date of its own, so its line says what is missing instead. */
export function issueLine(i: FeedIssue): string {
  const since = i.since ? ` since ${humanDate(i.since)}` : "";
  const good = i.last_good ? `last good ${humanDate(i.last_good)}` : "no good read on record";
  const why = i.why.replace(/\.+$/, "");
  if (i.state === "UNMATCHED") return `${feedName(i.feed)} is incomplete: ${why}.`;
  if (i.state === "RED") return `${feedName(i.feed)} is stale${since} (${good}): its source ${i.source_key ?? "upstream"} is RED in the data integrity system (${why}).`;
  return `${feedName(i.feed)} is stale${since} (${good}): ${why}.`;
}

export const FEED_ISSUES_COPY = {
  heading: "Some figures here use data that is out of date. Do not rely on them until it is fixed.",
  short: "Out-of-date data behind these figures:",
};
